import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart3, Plus, X, Send } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface CreatePollSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (pollJson: string) => void;
}

export function CreatePollSheet({ isOpen, onClose, onSend }: CreatePollSheetProps) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [allowMultiple, setAllowMultiple] = useState(false);

  const addOption = () => {
    if (options.length < 10) setOptions([...options, '']);
  };

  const removeOption = (i: number) => {
    if (options.length > 2) setOptions(options.filter((_, idx) => idx !== i));
  };

  const updateOption = (i: number, val: string) => {
    const next = [...options];
    next[i] = val;
    setOptions(next);
  };

  const validOptions = options.filter(o => o.trim());
  const canSend = question.trim() && validOptions.length >= 2;

  const handleSend = () => {
    if (!canSend) return;
    const poll = {
      question: question.trim(),
      options: validOptions.map((text, i) => ({ id: `opt_${i}`, text: text.trim(), voters: [] })),
      isAnonymous,
      allowMultiple,
    };
    onSend(JSON.stringify(poll));
    setQuestion(''); setOptions(['', '']); setIsAnonymous(false); setAllowMultiple(false);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-8 max-h-[85vh] overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Create Poll
          </SheetTitle>
        </SheetHeader>
        <div className="space-y-3">
          <Input
            placeholder="Ask a question *"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            className="rounded-xl"
          />

          <div className="space-y-2">
            {options.map((opt, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input
                  placeholder={`Option ${i + 1}${i < 2 ? ' *' : ''}`}
                  value={opt}
                  onChange={e => updateOption(i, e.target.value)}
                  className="rounded-xl flex-1"
                />
                {options.length > 2 && (
                  <button onClick={() => removeOption(i)} className="text-muted-foreground hover:text-destructive p-1">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {options.length < 10 && (
              <button
                onClick={addOption}
                className="flex items-center gap-2 text-sm text-primary font-medium px-1 py-1"
              >
                <Plus className="h-4 w-4" /> Add option
              </button>
            )}
          </div>

          <div className="flex items-center justify-between py-2">
            <Label htmlFor="anonymous" className="text-sm">Anonymous voting</Label>
            <Switch id="anonymous" checked={isAnonymous} onCheckedChange={setIsAnonymous} />
          </div>
          <div className="flex items-center justify-between py-2">
            <Label htmlFor="multiple" className="text-sm">Allow multiple votes</Label>
            <Switch id="multiple" checked={allowMultiple} onCheckedChange={setAllowMultiple} />
          </div>

          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="w-full rounded-xl h-11"
          >
            <Send className="h-4 w-4 mr-2" />
            Send Poll
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
